import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { AlertTriangle, Check, GitBranch, Loader2, RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import { cx } from '../../../lib/cx';
import { formatDateTime } from '../../../lib/format';
import { secretsQueryOptions } from '../api';
import { useRemoveSecret, useSetSecret } from '../useSettingsMutations';
import {
  addRepo,
  buildMap,
  disconnectGitHub,
  fetchAvailableRepos,
  fetchBranches,
  fetchBranchesForUrl,
  fetchConnection,
  fetchMap,
  fetchMappedRepos,
  removeRepo,
  setRepoBranch,
  startInstall,
  startRegistration,
  systemMapKeys,
  type MappedRepo,
  type SystemMapArea,
} from '../../system-map/api';
import { SetupSteps } from '../../system-map/SetupSteps';

/**
 * Repositories and the system map.
 *
 * ── WHAT THIS SCREEN IS FOR ──────────────────────────────────────────────
 * The estimate a client sees, and intake's ability to know what they are
 * talking about, both come from a map of the real codebase. This is where
 * that gets connected, checked and corrected.
 *
 * ── WHY THE CONNECT BUTTON IS A REDIRECT AND NOT A TOKEN FIELD ───────────
 * The admin is sent to GitHub, picks repositories there, and comes back.
 * Nothing is pasted here, so there is no credential in a browser, in a
 * password manager, or in this screen's memory — and the installation
 * belongs to the organisation rather than to whoever set it up.
 *
 * A token field still exists, one tab over in Integrations, for a
 * self-hosted deployment with no App registered. This screen says which one
 * is actually in use rather than showing "not connected" beside a working
 * map.
 */

const CARD = 'rounded-[10px] border border-line-strong p-4';

const COST_TONE: Record<string, string> = {
  low: 'bg-success-tint text-success',
  medium: 'bg-warn-tint text-warn',
  high: 'bg-danger-tint text-danger',
};

/** How often the screen re-reads the map while a build it started is running. */
const BUILD_POLL_MS = 5_000;

function Banner({ tone, children }: { readonly tone: 'ok' | 'warn'; readonly children: React.ReactNode }) {
  return (
    <div
      className={cx(
        'mb-4 flex items-start gap-2 rounded-[10px] border p-3 fs-12',
        tone === 'ok'
          ? 'border-success-border bg-success-tint text-ink'
          : 'border-warn-border bg-warn-tint text-ink',
      )}
    >
      {tone === 'ok' ? (
        <Check size={14} strokeWidth={2.5} className="mt-0.5 flex-none text-success" aria-hidden />
      ) : (
        <AlertTriangle size={14} strokeWidth={2} className="mt-0.5 flex-none text-warn" aria-hidden />
      )}
      <div>{children}</div>
    </div>
  );
}

function AreaCard({ area }: { readonly area: SystemMapArea }) {
  return (
    <div className="border-t border-line py-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="fs-13 font-semibold text-ink">{area.name}</span>
        <span className={cx('rounded-full px-2 py-0.5 fs-11 font-bold', COST_TONE[area.changeCost])}>
          {area.changeCost} change cost
        </span>
        {area.platforms.map((platform) => (
          <span
            key={platform}
            className="rounded-full bg-surface-3 px-2 py-0.5 fs-11 font-semibold text-ink-soft"
          >
            {platform}
          </span>
        ))}
      </div>
      <p className="mt-1.5 fs-12 text-muted">{area.summary}</p>
      {area.aliases.length > 0 && (
        <p className="mt-1 fs-11 text-faint">Also called: {area.aliases.join(', ')}</p>
      )}
      {area.sharesDataWith.length > 0 && (
        // The blast radius, given its own line because it is the single
        // field that most changes an estimate.
        <p className="mt-1 fs-11 text-faint">Shares data with: {area.sharesDataWith.join(', ')}</p>
      )}
      {area.notes !== '' && <p className="mt-1 fs-11 text-warn">{area.notes}</p>}
    </div>
  );
}

/**
 * The fine-grained token, for a deployment with no GitHub App registered.
 *
 * This used to live on its own Integrations tab beside three credential
 * slots that nothing read. It belongs here: it is one of two ways to answer
 * the same question this screen asks — how do we read the code — and reading
 * about it a tab away from the repositories it grants access to is how an
 * admin ends up with a token stored and no repository mapped.
 *
 * Write-only end to end. This component never receives a value back from the
 * server, only a status, so there is nothing here to render, log, or leave in
 * a browser history entry.
 */
function TokenField({ workspaceId }: { readonly workspaceId: string }) {
  const secrets = useQuery(secretsQueryOptions(workspaceId));
  const setSecret = useSetSecret(workspaceId);
  const removeSecret = useRemoveSecret(workspaceId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const secret = secrets.data?.find((row) => row.key === 'repo.github.credential');
  const configured = secret?.status === 'configured';
  const busy = setSecret.isPending || removeSecret.isPending;

  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="fs-12 font-semibold text-ink">Access token</span>
        {configured ? (
          <span className="rounded-full bg-success-tint px-2 py-0.5 fs-11 font-semibold text-success">
            Stored{secret?.updatedAt === null ? '' : ` · ${formatDateTime(secret.updatedAt)}`}
          </span>
        ) : (
          <span className="rounded-full bg-surface-3 px-2 py-0.5 fs-11 font-semibold text-muted">
            Not set
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(!editing)}
            className="rounded-lg border border-line-strong px-2.5 py-1 fs-11 font-semibold text-ink-soft hover:border-line-heavy disabled:opacity-50"
          >
            {configured ? 'Replace' : 'Add token'}
          </button>
          {configured && (
            <button
              type="button"
              disabled={busy}
              onClick={() => removeSecret.mutate('repo.github.credential')}
              className="fs-11 font-semibold text-muted hover:text-ink disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="github_pat_…"
            className="min-w-0 flex-1 basis-[15rem] rounded-lg border border-line-strong bg-transparent px-2.5 py-1.5 fs-12 placeholder:text-faint focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            disabled={busy || value.trim() === ''}
            onClick={() =>
              setSecret.mutate(
                { key: 'repo.github.credential', value: value.trim() },
                {
                  onSuccess: () => {
                    setValue('');
                    setEditing(false);
                  },
                },
              )
            }
            className="rounded-lg bg-ink px-3 py-1.5 fs-11 font-semibold text-white disabled:opacity-40"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setValue('');
            }}
            className="fs-11 font-semibold text-muted hover:text-ink"
          >
            Cancel
          </button>
        </div>
      )}

      <p className="mt-1.5 fs-11 text-faint">
        Encrypted before it is stored, and never shown again to anyone — including you.
      </p>
    </div>
  );
}

/**
 * Which branch this repository is mapped from.
 *
 * Not a detail. GitHub's "default branch" is frequently wrong in practice —
 * `main` left as an unused stub, or a default nobody ever set — and a map
 * built from a dead branch is worse than no map at all, because it reads as
 * authoritative while describing code that never ships.
 *
 * The list is fetched only when opened: it is a call to GitHub per
 * repository, and most people never change this.
 */
/**
 * Choosing the branch, then connecting — in that order.
 *
 * "Map this" is not just "add a row": it queues the first build, which is
 * the most expensive thing this product does. So the branch has to be
 * choosable BEFORE it, or the only way to correct a wrong default is to let
 * an Opus run happen against the wrong branch and then pay for a second one.
 *
 * That matters more than it sounds: `main` is frequently a dead stub here
 * while the work is on `develop`, so GitHub's default is often the branch
 * nobody has touched in a year.
 *
 * The branch list is fetched lazily, when the picker is opened, because most
 * repositories are mapped from their default and a list per repository on
 * every render would be an API call each for nothing.
 */
function MapThis({
  workspaceId,
  url,
  defaultBranch,
  busy,
  onMap,
}: {
  readonly workspaceId: string;
  readonly url: string;
  readonly defaultBranch: string;
  readonly busy: boolean;
  readonly onMap: (branch: string | undefined) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const branch = chosen ?? defaultBranch;

  const branches = useQuery({
    queryKey: systemMapKeys.branchesForUrl(workspaceId, url),
    queryFn: () => fetchBranchesForUrl(workspaceId, url),
    enabled: open,
  });

  return (
    <span className="inline-flex items-center gap-1.5">
      {open ? (
        <select
          autoFocus
          value={branch}
          disabled={branches.isPending}
          onChange={(event) => {
            setChosen(event.target.value);
            setOpen(false);
          }}
          onBlur={() => setOpen(false)}
          className="rounded-md border border-line-strong bg-base px-1.5 py-0.5 font-mono fs-11 text-ink"
        >
          {branches.isPending ? (
            <option value={branch}>Loading…</option>
          ) : (
            (branches.data?.branches ?? [defaultBranch]).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))
          )}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Which branch to build the map from — choose before mapping"
          className="inline-flex items-center gap-1 rounded-md bg-surface-3 px-2 py-0.5 font-mono fs-11 text-ink-soft hover:bg-surface"
        >
          <GitBranch size={11} strokeWidth={2} aria-hidden />
          {branch}
        </button>
      )}
      <button
        type="button"
        // Only send a branch when it is not GitHub's default: the server then
        // skips the extra round trip that verifies a branch exists.
        onClick={() => onMap(branch === defaultBranch ? undefined : branch)}
        disabled={busy}
        className="rounded-lg bg-ink px-2.5 py-1 fs-11 font-semibold text-white disabled:opacity-40"
      >
        Map this
      </button>
    </span>
  );
}

function BranchPicker({ workspaceId, repo }: { readonly workspaceId: string; readonly repo: MappedRepo }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const branches = useQuery({
    queryKey: systemMapKeys.branches(workspaceId, repo.id),
    queryFn: () => fetchBranches(workspaceId, repo.id),
    enabled: open,
  });

  const choose = useMutation({
    mutationFn: (branch: string) => setRepoBranch(workspaceId, repo.id, branch),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: systemMapKeys.repos(workspaceId) });
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Which branch the map is built from"
        className="inline-flex items-center gap-1 rounded-md bg-surface-3 px-2 py-0.5 font-mono fs-11 text-ink-soft hover:bg-surface"
      >
        <GitBranch size={11} strokeWidth={2} aria-hidden />
        {repo.defaultBranch}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <select
        autoFocus
        defaultValue={repo.defaultBranch}
        disabled={branches.isPending || choose.isPending}
        onChange={(event) => choose.mutate(event.target.value)}
        className="rounded-md border border-line-strong bg-base px-1.5 py-0.5 font-mono fs-11 text-ink"
      >
        {branches.isPending ? (
          <option>Loading…</option>
        ) : (
          (branches.data?.branches ?? [repo.defaultBranch]).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))
        )}
      </select>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="fs-11 font-semibold text-muted hover:text-ink"
      >
        Cancel
      </button>
    </span>
  );
}

export function RepositoriesSection({ workspaceId }: { readonly workspaceId: string }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const connection = useQuery({
    queryKey: systemMapKeys.connection(workspaceId),
    queryFn: () => fetchConnection(workspaceId),
  });
  const mapped = useQuery({
    queryKey: systemMapKeys.repos(workspaceId),
    queryFn: () => fetchMappedRepos(workspaceId),
  });
  const map = useQuery({
    queryKey: systemMapKeys.map(workspaceId),
    queryFn: () => fetchMap(workspaceId),
    // Poll whenever the SERVER says a build is outstanding. Driving this off
    // component state was the bug: walking to another page and back lost it,
    // and the panel then offered "Refresh now" over a build already running.
    //
    // The return type is annotated because the callback reads the query's own
    // data, which TypeScript cannot infer from inside its own options object.
    refetchInterval: (query): number | false =>
      (query.state.data?.build.state ?? null) === null ? false : BUILD_POLL_MS,
  });
  const available = useQuery({
    queryKey: systemMapKeys.available(workspaceId),
    queryFn: () => fetchAvailableRepos(workspaceId),
    enabled: connection.data?.connected === true,
  });

  // Server truth. `queued` means it is waiting out the coalescing delay
  // after a repository was connected; `running` means Opus is reading the
  // codebase right now.
  const info = map.data?.build;
  const build = info?.state ?? null;
  const building = build !== null;

  // Outstanding means outstanding: QUEUED locks the controls as firmly as
  // RUNNING does.
  //
  // Offering "Build now" during the coalescing window was a footgun dressed
  // as a convenience. That window exists so that mapping three repositories
  // in a row costs one Opus run instead of three — and the button sat there
  // inviting you to start the build after the FIRST one, which would have
  // mapped a third of the codebase and thrown away the saving the window had
  // just made. There is nothing to gain by starting 45 seconds early and a
  // whole map to lose.

  /**
   * Rebuilding is the most expensive thing this product does — several
   * repositories read and a long Opus pass — and the button that starts it
   * is one an impatient person presses twice. So it asks, and the asking
   * says what it costs rather than "are you sure?".
   */
  const [confirmBuild, setConfirmBuild] = useState<'incremental' | 'full' | null>(null);

  const [org, setOrg] = useState('');

  /**
   * Registration is a form POST the BROWSER makes, not a fetch: GitHub shows
   * the person a confirmation screen listing exactly what is about to be
   * created, and they have to see and accept it. So the server hands back
   * where to post and what to post, and this builds a throwaway form and
   * submits it.
   */
  const register = useMutation({
    mutationFn: () => startRegistration(workspaceId, org.trim()),
    onSuccess: (result) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = result.postUrl;
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'manifest';
      field.value = result.manifest;
      form.append(field);
      document.body.append(form);
      form.submit();
    },
  });

  const install = useMutation({
    mutationFn: () => startInstall(workspaceId),
    // A full-page navigation, deliberately: GitHub refuses to be framed, and
    // a popup here would be eaten by a blocker on the one click that matters.
    onSuccess: (result) => {
      globalThis.location.href = result.url;
    },
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectGitHub(workspaceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'system-map'] }),
  });

  const add = useMutation({
    mutationFn: ({ url, branch }: { url: string; branch?: string }) => addRepo(workspaceId, url, branch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'system-map'] }),
  });

  const drop = useMutation({
    mutationFn: (repoId: string) => removeRepo(workspaceId, repoId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'system-map'] }),
  });

  const requestBuild = useMutation({
    mutationFn: (full: boolean) => buildMap(workspaceId, full),
    // Re-read immediately so the button flips to "Building…" from server
    // state rather than from an optimistic guess that a navigation would lose.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemMapKeys.map(workspaceId) }),
  });

  const githubResult = params.get('github');
  useEffect(() => {
    if (githubResult === null) return;
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'system-map'] });
    // Strip the parameter so a refresh does not re-show the banner, and so
    // the URL is not something a person can be sent a fake version of.
    const next = new URLSearchParams(params);
    next.delete('github');
    setParams(next, { replace: true });
  }, [githubResult, params, setParams, queryClient, workspaceId]);

  const status = connection.data;
  const current = map.data?.map ?? null;

  return (
    <div className="space-y-4">
      {githubResult === 'connected' && (
        <Banner tone="ok">GitHub connected. Choose the repositories to map below.</Banner>
      )}
      {githubResult === 'registered' && (
        <Banner tone="ok">GitHub app created. Now connect it to this workspace.</Banner>
      )}
      {githubResult === 'register_failed' && (
        <Banner tone="warn">
          Creating the app didn&apos;t complete. GitHub gives the exchange one hour — try again.
        </Banner>
      )}
      {githubResult === 'failed' && (
        <Banner tone="warn">
          That didn&apos;t complete. The install link may have expired — try Connect again.
        </Banner>
      )}

      {/* ── Connection ───────────────────────────────────────────────── */}
      <section className={CARD}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="fs-13 font-semibold text-ink">GitHub</h2>
            <p className="mt-1 fs-12 text-muted">
              Read-only access to the code, so estimates reflect what a change really involves and intake
              knows what a client is referring to.
            </p>
          </div>

          {status?.connected === true ? (
            <button
              type="button"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="rounded-lg border border-line-strong px-3 py-1.5 fs-12 font-semibold text-ink-soft hover:border-line-heavy disabled:opacity-50"
            >
              Disconnect
            </button>
          ) : status?.appConfigured === true ? (
            <button
              type="button"
              onClick={() => install.mutate()}
              disabled={install.isPending}
              className="rounded-lg bg-ink px-3.5 py-2 fs-12 font-semibold text-white disabled:opacity-40"
            >
              {install.isPending ? 'Opening GitHub…' : 'Connect GitHub'}
            </button>
          ) : null}
        </div>

        {status?.connected === true && (
          <p className="mt-3 fs-12 text-ink-soft">
            Installed on <span className="font-semibold">{status.accountLogin}</span>
            {status.repositorySelection === 'all'
              ? ' — every repository in the organisation, including any added later.'
              : ' — only the repositories selected on GitHub.'}
          </p>
        )}

        {/* No App registered yet. Registration is one click — GitHub creates
            the App from a manifest, so nobody fills in a nine-field form or
            gets the webhook toggle wrong. The token guide stays as the
            fallback, collapsed. */}
        {status !== undefined && !status.appConfigured && (
          <div className="mt-3 rounded-[10px] border border-line bg-surface-2 p-3">
            <p className="fs-12 font-semibold text-ink">Set up GitHub once, for every workspace</p>
            <p className="mt-1 mb-2.5 fs-12 text-muted">
              GitHub creates the app for you with the right permissions already set. After this, every
              workspace connects with a single click.
            </p>

            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-0 flex-1 basis-[13rem]">
                <span className="fs-11 font-semibold text-ink-soft">GitHub organisation</span>
                <input
                  value={org}
                  onChange={(event) => setOrg(event.target.value)}
                  placeholder="noorix"
                  className="mt-1 w-full rounded-lg border border-line-strong bg-base px-2.5 py-1.5 fs-12 placeholder:text-faint focus:border-accent focus:outline-none"
                />
              </label>
              <button
                type="button"
                onClick={() => register.mutate()}
                disabled={register.isPending}
                className="rounded-lg bg-ink px-3.5 py-2 fs-12 font-semibold text-white disabled:opacity-40"
              >
                {register.isPending ? 'Opening GitHub…' : 'Create the app on GitHub'}
              </button>
            </div>
            <p className="mt-1.5 fs-11 text-faint">
              Leave the organisation blank to create it on your personal account — useful for a trial, but the
              app then belongs to you rather than the company.
            </p>

            <details className="mt-3">
              <summary className="cursor-pointer fs-11 font-semibold text-muted hover:text-ink">
                Or use a personal access token instead
              </summary>
              <p className="mt-1.5 fs-11 text-faint">{status.tokenCaveat}</p>
              <div className="mt-2">
                <SetupSteps steps={status.setupSteps} />
              </div>
              <TokenField workspaceId={workspaceId} />
            </details>
          </div>
        )}
      </section>

      {/* ── Which repositories ───────────────────────────────────────── */}
      {status?.connected === true && (
        <section className={CARD}>
          <h2 className="fs-13 font-semibold text-ink">Repositories to map</h2>
          <p className="mt-1 mb-3 fs-12 text-muted">
            Only what GitHub granted can be listed here. To change the list, alter it on GitHub and reconnect.
          </p>

          {available.isPending ? (
            <p className="fs-12 text-muted">Reading what GitHub granted…</p>
          ) : available.data?.repos.length === 0 ? (
            <p className="fs-12 text-muted">The App has no repositories yet. Add some on GitHub.</p>
          ) : (
            <ul className="space-y-1.5">
              {available.data?.repos.map((repo) => {
                const row = mapped.data?.repos.find(
                  (r) => r.url.toLowerCase() === repo.htmlUrl.toLowerCase(),
                );
                return (
                  <li
                    key={repo.fullName}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 truncate fs-12 font-medium text-ink">
                      {repo.fullName}
                    </span>
                    {row !== undefined && <BranchPicker workspaceId={workspaceId} repo={row} />}
                    {repo.private && (
                      <span className="rounded-full bg-surface-3 px-2 py-0.5 fs-11 font-semibold text-muted">
                        private
                      </span>
                    )}
                    {row !== undefined ? (
                      <button
                        type="button"
                        onClick={() => drop.mutate(row.id)}
                        disabled={drop.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-line-strong px-2.5 py-1 fs-11 font-semibold text-ink-soft hover:border-line-heavy disabled:opacity-50"
                      >
                        <Trash2 size={12} strokeWidth={1.75} aria-hidden />
                        Stop mapping
                      </button>
                    ) : (
                      <MapThis
                        workspaceId={workspaceId}
                        url={repo.htmlUrl}
                        defaultBranch={repo.defaultBranch}
                        busy={add.isPending}
                        onMap={(branch) => add.mutate({ url: repo.htmlUrl, branch })}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ── The map ──────────────────────────────────────────────────── */}
      <section className={CARD}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="fs-13 font-semibold text-ink">System map</h2>
            <p className="mt-1 fs-12 text-muted">
              Rebuilt automatically every Sunday, and only for what changed. Estimates record which version
              they were made against.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmBuild('incremental')}
              disabled={building || requestBuild.isPending || (mapped.data?.repos.length ?? 0) === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line-strong px-3 py-1.5 fs-12 font-semibold text-ink-soft hover:border-line-heavy disabled:opacity-50"
            >
              {building ? (
                <Loader2 size={13} strokeWidth={2} className="animate-spin" aria-hidden />
              ) : (
                <RefreshCw size={13} strokeWidth={1.75} aria-hidden />
              )}
              {build === 'running' ? 'Building…' : build === 'queued' ? 'Starting…' : 'Refresh now'}
            </button>
            {current !== null && (
              <button
                type="button"
                onClick={() => setConfirmBuild('full')}
                disabled={building || requestBuild.isPending}
                className="fs-11 font-semibold text-muted hover:text-ink disabled:opacity-50"
              >
                Rebuild from scratch
              </button>
            )}
          </div>
        </div>

        {confirmBuild !== null && (
          <div className="mt-3 rounded-[10px] border border-warn-border bg-warn-tint p-3">
            <p className="flex items-start gap-1.5 fs-12 font-semibold text-ink">
              <TriangleAlert size={13} strokeWidth={2} className="mt-0.5 flex-none text-warn" aria-hidden />
              This spends AI credits
            </p>
            <p className="mt-1 fs-12 text-ink-soft">
              {confirmBuild === 'full' || current === null
                ? 'A full build reads every connected repository and runs a long pass over the whole codebase. It is the most expensive thing Rundown does.'
                : 'This re-reads what changed since the last build and re-maps only those areas. Cheaper than a full rebuild, but still a model run over real code.'}
            </p>
            <p className="mt-1 fs-11 text-muted">
              The map rebuilds itself every Sunday, and connecting a repository builds it too — so do this
              only after a change big enough that waiting would give someone a wrong estimate.
            </p>
            <div className="mt-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  requestBuild.mutate(confirmBuild === 'full');
                  setConfirmBuild(null);
                }}
                className="rounded-lg bg-ink px-3 py-1.5 fs-12 font-semibold text-white"
              >
                {confirmBuild === 'full' ? 'Rebuild everything' : 'Build now'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmBuild(null)}
                className="rounded-lg px-2.5 py-1.5 fs-12 font-semibold text-muted hover:text-ink"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/*
          ONE line while a build is outstanding, not two.
          
          This block and the "no map yet" block below both used to render
          during a build, so the screen said "Starting shortly…" and
          "Building the first map now" at the same time — two different
          answers to the same question. Everything there is to say about an
          outstanding build is said here, and the block below now stays quiet
          while one is running.
        */}
        {building && (
          <p className="mt-3 fs-11 text-faint">
            {build === 'queued'
              ? 'Starting in a moment — waiting briefly so that mapping several repositories in a row builds once rather than once each.'
              : // The live step when the build is reporting one, and a general
                // sentence when it is not — which is what a build that
                // survived an API restart looks like.
                (info?.progress?.detail ??
                'Reading the repositories and working out what changed. This takes a few minutes.')}
            {build === 'running' && ' It keeps going if you leave this page.'}
            {current === null && ' Estimates stay ungrounded until it lands.'}
          </p>
        )}

        {current === null ? (
          <p className="mt-3 fs-12 text-muted">
            {(mapped.data?.repos.length ?? 0) === 0
              ? 'Connect a repository first, then build the map.'
              : building
                ? // Said above, in the one place that describes an outstanding
                  // build. Repeating it here is what produced two messages.
                  null
                : info?.lastFailed === true
                  ? `The last build failed${info.lastAttemptAt === null ? '' : ` at ${formatDateTime(info.lastAttemptAt)}`}. Nothing was stored, so estimates are still ungrounded — try again, and if it fails twice the reason is in the server log.`
                  : 'No map yet. Build one to ground estimates in the real codebase.'}
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2 fs-11 text-muted">
              <span className="rounded-full bg-surface-3 px-2 py-0.5 font-semibold">v{current.version}</span>
              <span>built {formatDateTime(current.builtAt)}</span>
              <span>· {current.content.areas.length} areas</span>
              {current.reviewedAt === null ? (
                <span className="text-warn">· not yet checked by a person</span>
              ) : (
                <span className="text-success">· checked {formatDateTime(current.reviewedAt)}</span>
              )}
            </div>

            {current.content.platformNote !== '' && (
              <p className="mt-2.5 rounded-lg bg-surface-2 p-2.5 fs-12 text-ink-soft">
                {current.content.platformNote}
              </p>
            )}

            <div className="mt-3">
              {current.content.areas.map((area) => (
                <AreaCard key={area.name} area={area} />
              ))}
            </div>

            {current.content.glossary.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer fs-12 font-semibold text-accent">
                  What people here call things ({current.content.glossary.length})
                </summary>
                <ul className="mt-2 space-y-1 fs-11 text-muted">
                  {current.content.glossary.map((entry) => (
                    <li key={entry.term}>
                      <span className="font-semibold text-ink-soft">{entry.term}</span> ({entry.area}) —{' '}
                      {entry.means}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        {(requestBuild.error ?? add.error ?? drop.error) !== null && (
          <p role="alert" className="mt-3 fs-11 text-danger">
            {(requestBuild.error ?? add.error ?? drop.error)?.message}
          </p>
        )}
      </section>
    </div>
  );
}
